import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import { collection, doc, getDocs, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { toast } from "sonner";
import { cn, db, manageJob, useAuth } from "@um/ui";
import { PersonaImage } from "@um/ui/components";
import { PersonaImageCollection, getAgent, getPersona } from "@um/contracts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCharactersStore } from "@/store/useCharactersStore";
import { useProfilesStore } from "@/store/useProfilesStore";
import { useSparkStore } from "@/store/useSparkStore";

/**
 * Spark an on-demand story: pick a profile, a character, and a prompt (from a
 * template or free text), then kick off a one-off storyTeller run.
 *
 * Opened via `useSparkStore.openSpark()` and mounted once at the app root so
 * it is reachable from every route — the FAB used to own it, but the support
 * menu at /routines renders outside MainLayout and needs it too. Mirrors the
 * SupportDrawer pattern.
 */
export function SparkStoryDialog() {
  const navigate = useNavigate();
  const { accountId, userMind, role } = useAuth();
  const { isOpen, close } = useSparkStore();
  const { profiles, subscribeToProfiles, unsubscribeFromProfiles } = useProfilesStore();
  const { characters, subscribeToCharacters, unsubscribeFromCharacters } =
    useCharactersStore();

  // The dialog is mounted at the app root, so on routes that don't already
  // populate these stores (notably /routines, which renders outside
  // MainLayout) the picker would come up empty. Claim the listeners while the
  // dialog is open, using the same ownership rules MainLayout follows:
  // subscribeToProfiles is idempotent per account, but subscribeToCharacters
  // tears down whatever listener exists — so only claim its single slot when
  // it is free, and only release what we claimed.
  useEffect(() => {
    if (!isOpen || !accountId) return;

    let claimedCharactersSlot = false;
    subscribeToProfiles(accountId);
    if (!useCharactersStore.getState().unsubscribeSnapshot) {
      subscribeToCharacters(accountId);
      claimedCharactersSlot = true;
    }

    return () => {
      unsubscribeFromProfiles();
      if (claimedCharactersSlot) unsubscribeFromCharacters();
    };
  }, [
    isOpen,
    accountId,
    subscribeToProfiles,
    unsubscribeFromProfiles,
    subscribeToCharacters,
    unsubscribeFromCharacters,
  ]);

  const [selectedMindId, setSelectedMindId] = useState<string>("");
  const [selectedAnimalId, setSelectedAnimalId] = useState<string>("");
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState<number | null>(0);
  const [customPrompt, setCustomPrompt] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeJobForMind, setActiveJobForMind] = useState<any | null>(null);

  // Helper to retrieve a friendly greeting/neutral avatar for v2 character/mind personas
  const getFriendlyAvatar = (persona: any) => {
    if (!persona) return null;
    const col = new PersonaImageCollection(persona);
    return col.lookup("A14") || col.lookup("A5") || col.lookup("A16") || col.getDefault();
  };

  // Listen to active storytelling jobs for the selected child (Mind)
  useEffect(() => {
    if (!selectedMindId || !accountId) {
      setActiveJobForMind(null);
      return;
    }

    const jobsCol = collection(db, "Account", accountId, "Job");
    const q = query(
      jobsCol,
      where("mind", "==", doc(db, "Account", accountId, "Mind", selectedMindId)),
      where("handlerName", "==", "storyTeller"),
      where("status", "in", ["active", "paused", "review"])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const jobDoc = snapshot.docs[0];
        const jobData = { id: jobDoc.id, ...jobDoc.data() } as any;
        setActiveJobForMind(jobData);
      } else {
        setActiveJobForMind(null);
      }
    });

    return () => unsubscribe();
  }, [selectedMindId, accountId]);

  // Default selectedAnimalId to active job's animal if the active job changes
  useEffect(() => {
    if (isOpen && activeJobForMind?.agent?.animal?.id) {
      setSelectedAnimalId(activeJobForMind.agent.animal.id);
    }
  }, [activeJobForMind, isOpen]);

  // Auto-select first child and character when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (userMind?.ref.id) {
        setSelectedMindId(userMind.ref.id);
      } else if (profiles.length > 0 && !selectedMindId) {
        setSelectedMindId(profiles[0].ref.id);
      }
      if (characters.length > 0 && !selectedAnimalId) {
        setSelectedAnimalId(characters[0].ref.id);
      }
    }
  }, [isOpen, userMind?.ref.id, profiles.length, characters.length]);

  const selectedMind = profiles.find((p) => p.ref.id === selectedMindId);
  const selectedAnimal = characters.find((c) => c.ref.id === selectedAnimalId);

  // Derive character name and role from the default/active persona
  const activePersona = selectedAnimal 
    ? getPersona(selectedAnimal)
    : null;

  const childName = selectedMind?.name || "Zia";
  const characterName = selectedAnimal?.name || activePersona?.name || "Peeps";

  // Resolve profile/character images
  const selectedMindActivePersona = selectedMind ? getPersona(selectedMind) : null;
  const selectedMindProfileImage = getFriendlyAvatar(selectedMindActivePersona);

  const selectedAnimalPersonaImage = getFriendlyAvatar(activePersona);
  
  // Resolve the role from the animal's roles map for this persona
  const activePersonaId = activePersona?.id;
  const roleInfo = activePersonaId && selectedAnimal?.roles?.[activePersonaId];
  const characterRole =
    (roleInfo && typeof roleInfo === "object" && "title" in roleInfo
      ? roleInfo.title
      : null) || "support partner";

  const templates = [
    {
      label: "🌅 Wake Up",
      prompt: `${childName} is tired and needs a wake up story. Have ${characterName} the ${characterRole} give ${childName} a fun story to get their day started.${childName.toLowerCase() === "zia" ? " Include the ghost of Humphrey." : ""}`,
    },
    {
      label: "🦷 Brush Teeth",
      prompt: `${childName} doesn't want to brush their teeth. Have ${characterName} tell a magical story about sparkling tooth gems.`,
    },
    {
      label: "👟 Put on Shoes",
      prompt: `${childName} is lagging on getting dressed. Have ${characterName} tell a story about super-speed shoelaces.`,
    },
    {
      label: "🥦 Eat Veggies",
      prompt: `${childName} is refusing to eat dinner. Have ${characterName} tell a story about a superhero who gets power from green veggies.`,
    },
    {
      label: "🧸 Clean Up",
      prompt: `${childName} doesn't want to tidy up. Have ${characterName} tell a story about toys that come alive and love being parked in their cozy bins.`,
    },
    {
      label: "🤝 Sharing",
      prompt: `${childName} is finding it hard to share. Have ${characterName} tell a story about the magical joy of playing together and sharing toys.`,
    },
    {
      label: "🌙 Bedtime",
      prompt: `${childName} is restless and needs to sleep. Have ${characterName} tell a gentle, calming story that helps them drift off to sleep.`,
    },
  ];

  const activePromptValue = selectedTemplateIndex !== null
    ? templates[selectedTemplateIndex]?.prompt || ""
    : customPrompt;

  const handleSparkStory = async () => {
    if (!accountId || !selectedMindId || !selectedAnimalId || !activePromptValue.trim()) return;
    setIsGenerating(true);
    try {
      // 1. Search if there is an existing active/paused/review job for this mindId and animalId with handlerName = "storyTeller".
      const jobsCol = collection(db, "Account", accountId, "Job");
      const q = query(
        jobsCol,
        where("mind", "==", doc(db, "Account", accountId, "Mind", selectedMindId)),
        where("handlerName", "==", "storyTeller"),
        where("status", "in", ["active", "paused", "review"])
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // Use the first existing job
        const jobDoc = snapshot.docs[0];
        const jobId = jobDoc.id;
        const jobData = jobDoc.data();
        
        // If the selected character is different from the job's current character, update it!
        const currentAnimalId = jobData.references?.animal?.id;
        if (currentAnimalId !== selectedAnimalId && selectedAnimal) {
          const newAgent = getAgent(selectedAnimal);
          await updateDoc(jobDoc.ref, {
            agent: newAgent,
            "references.animal": selectedAnimal.ref,
          });
        }
        
        // Call the sparkStory action
        const actionResult = await manageJob({
          type: "action",
          accountId,
          jobId,
          actionId: "sparkStory",
          input: { prompt: activePromptValue },
        });
        
        const data = actionResult.data;
        if (data && "handleJobTaskId" in data && data.handleJobTaskId) {
          close();
          setSelectedTemplateIndex(0);
          setCustomPrompt("");
          toast.success("Generating your custom story and missions...");
          navigate(`/wait-for-task/${data.handleJobTaskId}`);
        } else {
          throw new Error("Failed to trigger story generation task");
        }
      } else {
        toast.info("Initializing story agent and generating story...");
        // Create a new storyTeller job with the prompt to trigger immediate generation in the backend
        const createResult = await manageJob({
          type: "create",
          accountId,
          handlerName: "storyTeller",
          mindId: selectedMindId,
          animalId: selectedAnimalId,
          input: {
            overallGoal: "On-demand adventure stories",
            schedule: "0 19 * * *",
            castSelectionCriteria: "The reader; characters can be inferred from the story.",
            sparkStoryPrompt: activePromptValue,
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        
        const data = createResult.data;
        if (data && "handleJobTaskId" in data && data.handleJobTaskId) {
          close();
          setSelectedTemplateIndex(0);
          setCustomPrompt("");
          toast.success("Initializing agent and generating your first custom story...");
          navigate(`/wait-for-task/${data.handleJobTaskId}`);
        } else {
          throw new Error("Failed to create background story agent");
        }
      }
    } catch (err: any) {
      console.error("Error sparking story:", err);
      toast.error(err.message || "Failed to spark story");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-md p-0 bg-white border border-slate-200 text-slate-800 rounded-3xl overflow-hidden flex flex-col shadow-xl">
        {profiles.length === 0 ? (
          <div className="p-6 text-center space-y-4">
            <p className="text-slate-600 text-sm">You need a child profile first to generate stories.</p>
            <Button
              onClick={() => {
                close();
                navigate("/profiles");
              }}
              className="bg-brand-secondary hover:bg-brand-secondary/90 text-white font-medium rounded-xl text-sm"
            >
              Go to Profiles
            </Button>
          </div>
        ) : characters.length === 0 ? (
          <div className="p-6 text-center space-y-4">
            <p className="text-slate-600 text-sm">You need to adopt a character first to tell stories.</p>
            <Button
              onClick={() => {
                close();
                navigate("/adoption-center");
              }}
              className="bg-brand-secondary hover:bg-brand-secondary/90 text-white font-medium rounded-xl text-sm"
            >
              Go to Adoption Hub
            </Button>
          </div>
        ) : (
          <>
            {/* Top Character Header (Overflowing sprite) */}
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 flex flex-row items-center gap-4 space-y-0 text-left bg-gradient-to-b from-slate-50/80 to-transparent">
              {/* Character Avatar/Image */}
              <div className="relative w-14 h-14 shrink-0 flex items-center justify-center">
                <div className="absolute inset-1 rounded-2xl bg-brand-mint/30 border border-brand-teal/10 w-12 h-12" />
                <div className="relative w-16 h-16 z-10 -mt-2">
                  {selectedAnimal ? (
                    (() => {
                      const activePers = getPersona(selectedAnimal);
                      const selectedAnimalPersonaImage = getFriendlyAvatar(activePers);
                      return selectedAnimalPersonaImage ? (
                        <PersonaImage
                          image={selectedAnimalPersonaImage}
                          alt={characterName}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <img
                          src="/assets/emberlee.svg"
                          alt={characterName}
                          className="w-full h-full object-contain"
                        />
                      );
                    })()
                  ) : (
                    <img
                      src="/assets/emberlee.svg"
                      alt="Emberlee"
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-1.5">
                  <Sparkles className="w-5 h-5 text-brand-teal animate-pulse" />
                  Spark On-Demand Story
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5 truncate">
                  Told by <span className="font-semibold text-slate-700">{characterName}</span> for <span className="font-semibold text-slate-700">{childName}</span>
                </DialogDescription>
              </div>
            </DialogHeader>

            {/* Main Dialog Form Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-white">
              
              {/* For Whom (Full-width child selector) */}
              {role !== "member" && (
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block px-1">For Whom</span>
                  <div className="flex gap-2 overflow-x-auto py-2.5 px-1 scrollbar-none bg-slate-50/50 p-2.5 rounded-2xl border border-slate-100">
                    {profiles.map((profile) => {
                      const activePers = getPersona(profile);
                      const imageInfo = getFriendlyAvatar(activePers);
                      const isSelected = selectedMindId === profile.ref.id;
                      return (
                        <button
                          key={profile.ref.id}
                          type="button"
                          onClick={() => setSelectedMindId(profile.ref.id)}
                          className={cn(
                            "flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all shrink-0 cursor-pointer text-left shadow-xs hover:scale-102",
                            isSelected
                              ? "bg-brand-teal/10 border-brand-teal text-brand-teal font-bold scale-102"
                              : "bg-white border-slate-200/80 text-slate-600 hover:border-slate-300 hover:text-slate-800"
                          )}
                          disabled={isGenerating}
                        >
                          <div className="w-5 h-5 rounded-full overflow-hidden border border-slate-200 bg-white shrink-0">
                            {imageInfo ? (
                              <PersonaImage image={imageInfo} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-500 text-[9px] font-bold">
                                {profile.name?.charAt(0) || "?"}
                              </div>
                            )}
                          </div>
                          <span className="text-xs">
                            {profile.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Told By (Only shown if no active routine, otherwise shows a gorgeous locked status card) */}
              {/* Told By */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center px-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Told By</span>
                  {activeJobForMind && (
                    <span className="text-[9px] font-bold bg-brand-teal/15 text-brand-teal px-2 py-0.5 rounded-full uppercase tracking-wider">
                      Routine Active
                    </span>
                  )}
                </div>
                <div className="flex gap-2 overflow-x-auto py-2.5 px-1 scrollbar-none bg-slate-50/50 p-2.5 rounded-2xl border border-slate-100">
                  {characters.map((char) => {
                    const activePers = getPersona(char);
                    const imageInfo = getFriendlyAvatar(activePers);
                    const isSelected = selectedAnimalId === char.ref.id;
                    const isRoutineActive = activeJobForMind?.agent?.animal?.id === char.ref.id;
                    return (
                      <button
                        key={char.ref.id}
                        type="button"
                        onClick={() => setSelectedAnimalId(char.ref.id)}
                        className={cn(
                          "flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all shrink-0 cursor-pointer text-left relative shadow-xs hover:scale-102",
                          isSelected
                            ? "bg-brand-coral/10 border-brand-coral text-brand-coral font-bold scale-102"
                            : "bg-white border-slate-200/80 text-slate-600 hover:border-slate-300 hover:text-slate-800"
                        )}
                        disabled={isGenerating}
                      >
                        <div className="w-5 h-5 shrink-0 relative flex items-center justify-center">
                          {/* Overflowing sprite, sits outside the circle! */}
                          <div className="absolute w-8 h-8 -mt-2 z-10">
                            {imageInfo ? (
                              <PersonaImage image={imageInfo} className="w-full h-full object-contain" />
                            ) : (
                              <div className="w-full h-full bg-slate-100 rounded-full flex items-center justify-center text-slate-500 text-[10px]">
                                ?
                              </div>
                            )}
                          </div>
                        </div>
                        <span className="text-xs pl-2.5 flex items-center gap-1.5">
                          {char.name || activePers?.name || "Unnamed"}
                          {isRoutineActive && (
                            <span className="w-1.5 h-1.5 rounded-full bg-brand-teal" title="Current routine storyteller" />
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Prompt input */}
              <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">What should the story be about?</label>
                  {activePromptValue && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTemplateIndex(null);
                        setCustomPrompt("");
                      }}
                      className="text-[10px] font-bold text-slate-400 hover:text-brand-coral transition-colors px-2 py-0.5 rounded-full hover:bg-slate-100 cursor-pointer"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="relative">
                  <textarea
                    value={activePromptValue}
                    onChange={(e) => {
                      setSelectedTemplateIndex(null);
                      setCustomPrompt(e.target.value);
                    }}
                    placeholder={`e.g. ${characterName} helps ${childName} get dressed for school because they are too tired...`}
                    className="w-full h-24 px-4 py-3 text-sm bg-slate-50/30 border border-slate-200/80 rounded-2xl text-slate-800 placeholder-slate-400/85 focus:outline-none focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal focus:bg-white transition-all shadow-inner resize-none"
                    disabled={isGenerating}
                  />
                </div>
              </div>

              {/* Presets */}
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Quick Presets</label>
                <div className="flex flex-wrap gap-2">
                  {templates.map((preset, index) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => setSelectedTemplateIndex(index)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer shadow-xs hover:scale-102",
                        selectedTemplateIndex === index
                          ? "bg-brand-coral border-brand-coral text-white font-bold"
                          : "bg-white hover:bg-slate-50 text-slate-600 border-slate-200/70 hover:border-slate-300"
                      )}
                      disabled={isGenerating}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer / Submit */}
            <div className="flex justify-end gap-3 shrink-0 bg-slate-50 border-t border-slate-100 p-4">
              <Button
                variant="ghost"
                type="button"
                onClick={() => close()}
                disabled={isGenerating}
                className="rounded-xl cursor-pointer"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSparkStory}
                disabled={isGenerating || !activePromptValue.trim()}
                className="bg-brand-teal hover:bg-brand-teal/90 text-white font-bold rounded-xl px-5 shadow-md shadow-brand-teal/15 transition-all cursor-pointer"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Spark Story</span>
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
